import { memo, useMemo } from "react";

import { useCellActions } from "../hooks/useCellActions";
import { BaseCell } from "../primitives";
import { useCellDisplayData } from "../hooks/useCellDisplayData";
import { BooleanCell } from "../primitives/BooleanCell";
import { useGridContext } from "../../../hooks/useEvaluateGridContext";

import { LLMAssertionCell } from "./AssertionsCell";
import { HumanInputCell } from "./HumanInputCell";
import { FunctionCallCell } from "./FunctionCallCell";
import { CompareDiffCell } from "./CompareDiffCell";

import { truncate } from "@/utils/strings";
import { ColumnType, ReportCell, ReportColumn } from "@/types/evaluate";
import { useReportContext } from "@/components/Evaluate/report-context";

interface CellProps {
  cell: ReportCell;
  column: ReportColumn;
}

export const ComplexCell = memo<CellProps>(({ cell, column }) => {
  const { selectedColumn } = useReportContext();
  const isColumnSelected = selectedColumn?.id === column.id;
  const { tableStore } = useGridContext();
  const selectedCell = tableStore?.selectedCell;

  const isPromptOrAgent =
    column.column_type === ColumnType.PROMPT_TEMPLATE || column.column_type === ColumnType.WORKFLOW;
  const { actions } = useCellActions({
    cell,
    column,
    showOpenRequest: isPromptOrAgent,
  });

  const { displayValue, shouldTruncate } = useCellDisplayData(cell, column, false, selectedCell);

  const cellContent = useMemo(() => {
    // eslint-disable-next-line default-case
    switch (column.column_type) {
      case ColumnType.LLM_ASSERTION:
        return <LLMAssertionCell cell={cell} column={column} isColumnSelected={isColumnSelected} />;

      case ColumnType.HUMAN:
        return <HumanInputCell cell={cell} column={column} isColumnSelected={isColumnSelected} />;

      case ColumnType.COMPARE:
        if (
          (typeof cell.display_value.value === "boolean" && !cell.display_value.value) ||
          (typeof cell.display_value.value === "string" && cell.display_value.value !== "")
        ) {
          return <CompareDiffCell key={`${cell?.id}-compare`} cell={cell} />;
        }
        return <BooleanCell value isSelected={isColumnSelected} showColors={!isColumnSelected} />;
    }
    // output_media: backend may send type "output_media" or type "object" with value.type === "output_media" (legacy)
    const rawValue = cell.display_value.value as
      | { type?: string; url?: string; media_type?: string }
      | undefined;
    let mediaPayload: { url?: string; media_type?: string } | null = null;
    if (cell.display_value.type === "output_media") {
      mediaPayload = rawValue ?? null;
    } else if (rawValue?.type === "output_media") {
      mediaPayload = rawValue;
    }
    if (mediaPayload?.url && (mediaPayload.media_type === "image" || !mediaPayload.media_type)) {
      return (
        <div className="flex h-full w-full items-center justify-center overflow-hidden">
          <img src={mediaPayload.url} alt="Generated" className="h-full w-full object-cover" />
        </div>
      );
    }
    if (mediaPayload && !mediaPayload.url) {
      return <div className="p-2 text-xs text-gray-500">—</div>;
    }

    // eslint-disable-next-line default-case
    switch (cell.display_value.type) {
      case "function":
      case "tool_calls":
        return <FunctionCallCell cell={cell} column={column} isColumnSelected={isColumnSelected} />;

      case "boolean":
        return (
          <BooleanCell
            value={cell.display_value.value as boolean}
            isSelected={isColumnSelected}
            showColors={!isColumnSelected}
          />
        );

      case "unknown":
        return (
          <div className="flex items-center justify-center p-2 text-xs text-gray-700">
            {displayValue ||
              (typeof cell?.value?.value === "string"
                ? cell?.value?.value
                : JSON.stringify(cell?.value?.value)) ||
              "—"}
          </div>
        );
    }

    const finalDisplayValue = shouldTruncate ? truncate(displayValue, 5000) : displayValue;

    return (
      <div className="h-full w-full whitespace-pre-wrap break-words p-2 text-xs text-gray-700">
        {finalDisplayValue}
      </div>
    );
  }, [cell, column, isColumnSelected, displayValue, shouldTruncate]);

  return (
    <BaseCell isSelected={isColumnSelected} actions={actions}>
      {cellContent}
    </BaseCell>
  );
});
